import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaInstagram, FaSoundcloud, FaWhatsapp } from "react-icons/fa";
import api, { API_BASE } from "../services/api";
import { submissionsApi } from "../services/externalPlatformApi";
import SmartImg from "../components/SmartImg";
import Talent3DStage from "../components/Talent3DStage";

const imgSrc = (url) => (url?.startsWith("http") ? url : `${API_BASE}${url || ""}`);
const WHATSAPP_SHORT_LINK = "https://wa.me/message/5UT3QJZMUBKNP1";
const PUBLIC_ROSTER_TIMEOUT_MS = 3500;

const withRosterTimeout = (promise) => {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error("Roster request timed out")), PUBLIC_ROSTER_TIMEOUT_MS);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
};

const normaliseRosterPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.roster)) return payload.roster;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normaliseRosterCategory = (value) => String(value || "").trim().toUpperCase();
const rosterCategory = (item) => normaliseRosterCategory(item.category || item.type || item.role);

const BENEFITS = [
  "Bookings at HLT events",
  "Platform exposure",
  "Affiliate income",
  "Industry network",
  "A&R consideration",
  "Priority submission review",
];

const EMPTY_APPLICATION = {
  artist_name: "",
  email: "",
  location: "",
  instagram: "",
  soundcloud: "",
  genre: "",
  bio: "",
  why_hlt: "",
};

export default function Roster() {
  const [djs, setDjs] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function loadRoster() {
      setLoading(true);
      setErr("");
      try {
        const { data } = await withRosterTimeout(api.get("/api/roster", { timeout: PUBLIC_ROSTER_TIMEOUT_MS }));
        const roster = normaliseRosterPayload(data).filter((item) => item?.active !== false);
        const djItems = roster.filter((item) => rosterCategory(item) === "DJ");
        const creatorItems = roster.filter((item) => ["INFLUENCER", "CREATOR"].includes(rosterCategory(item)));

        if (mounted) {
          setDjs(djItems);
          setCreators(creatorItems);
        }
      } catch {
        if (mounted) {
          setDjs([]);
          setCreators([]);
          setErr("");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadRoster();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="page space-y-10">
      <Talent3DStage>
        <div className="relative z-10 flex min-h-[34rem] items-end px-5 py-8 md:px-8 md:py-10">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.28em] text-brand-500">
              HLT talent portal
            </p>
            <h1 className="mt-3 font-display text-5xl md:text-6xl leading-none">
              Emerging <span className="text-brand-500">Talent</span> Roster
            </h1>
            <p className="mt-4 max-w-2xl text-slate-300">
              The HLT roster is our curated house music talent network: DJs, creators and cultural voices
              we can showcase, book, develop and connect to real opportunities.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button className="btn-primary" onClick={() => setModalOpen(true)}>
                Apply to roster
              </button>
              <Link to="/submit" className="btn-secondary">
                Submit a mix first
              </Link>
              <a
                href={WHATSAPP_SHORT_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <FaWhatsapp /> Management
              </a>
            </div>
          </div>
        </div>
      </Talent3DStage>

      <section>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-3xl">What HLT provides</h2>
            <p className="mt-1 text-slate-400">The roster is built to create movement, not just profile pages.</p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((benefit, index) => (
            <div key={benefit} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <span className="text-xs text-brand-500">0{index + 1}</span>
              <h3 className="mt-2 text-lg font-semibold text-white">{benefit}</h3>
            </div>
          ))}
        </div>
      </section>

      {err && (
        <section className="card border-amber-500/20 bg-amber-500/10 text-amber-100">
          {err}
        </section>
      )}

      {loading ? (
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="card animate-pulse">
              <div className="aspect-[4/5] rounded-xl bg-white/5" />
              <div className="mt-4 h-5 w-2/3 rounded bg-white/5" />
              <div className="mt-2 h-4 w-1/2 rounded bg-white/5" />
            </div>
          ))}
        </section>
      ) : (
        <>
          <RosterSection title="DJs" items={djs} category="dj" onApply={() => setModalOpen(true)} />
          <RosterSection title="Creators" items={creators} category="influencer" onApply={() => setModalOpen(true)} />
        </>
      )}

      <section className="card">
        <div className="grid gap-5 md:grid-cols-[1fr,18rem] md:items-center">
          <div>
            <h2 className="font-display text-3xl">Selection criteria</h2>
            <p className="mt-2 text-slate-300">
              We look for sound quality, professionalism, potential to break, alignment with HLT culture,
              and stage or camera presence.
            </p>
          </div>
          <button className="btn-primary" onClick={() => setModalOpen(true)}>
            Apply now
          </button>
        </div>
      </section>

      {modalOpen && <ApplicationModal onClose={() => setModalOpen(false)} />}
    </div>
  );
}

function RosterSection({ title, items, category, onApply }) {
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl">{title}</h2>
          <p className="text-slate-400">
            {items.length ? `${items.length} profile${items.length === 1 ? "" : "s"} live` : "Open for applications"}
          </p>
        </div>
        <button className="btn-secondary" onClick={onApply}>Apply</button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <TalentCard key={item.id} item={item} category={category} />
        ))}
        {[...Array(Math.max(1, 3 - items.length))].map((_, index) => (
          <OpenSlot key={`${title}-${index}`} title={title} onApply={onApply} />
        ))}
      </div>
    </section>
  );
}

function TalentCard({ item, category }) {
  return (
    <article className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition hover:-translate-y-1 hover:border-brand-600/50">
      <Link to={`/talent/${category}/${item.slug}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden bg-black">
          {item.imageUrl ? (
            <SmartImg
              src={imgSrc(item.imageUrl)}
              alt={item.name}
              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
              width={520}
              height={650}
            />
          ) : (
            <div className="grid h-full place-items-center text-slate-500">No image</div>
          )}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-4">
            <h3 className="text-2xl font-semibold text-white">{item.name}</h3>
            <p className="mt-1 text-sm text-slate-300">{item.location || "HLT roster"}</p>
          </div>
        </div>
      </Link>
      <div className="p-4">
        <div className="flex flex-wrap gap-2">
          {item.genre && <span className="badge">{item.genre}</span>}
          {item.inspiration && <span className="badge">A&R watched</span>}
        </div>
        {item.blurb && <p className="mt-3 line-clamp-3 text-sm text-slate-300">{item.blurb}</p>}
        <div className="mt-4 flex items-center gap-2">
          {item.instagramHandle && (
            <a href={normaliseSocial(item.instagramHandle, "instagram")} target="_blank" rel="noopener noreferrer" className="btn-secondary px-3">
              <FaInstagram />
            </a>
          )}
          {item.soundcloudUrl && (
            <a href={item.soundcloudUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary px-3">
              <FaSoundcloud />
            </a>
          )}
          <Link to={`/talent/${category}/${item.slug}`} className="btn-primary ml-auto">
            View profile
          </Link>
        </div>
      </div>
    </article>
  );
}

function OpenSlot({ title, onApply }) {
  return (
    <article className="grid min-h-[28rem] place-items-center rounded-2xl border border-dashed border-brand-600/50 bg-brand-600/5 p-6 text-center">
      <div>
        <p className="text-xs uppercase tracking-[0.28em] text-brand-500">Open slot</p>
        <h3 className="mt-3 text-2xl font-semibold text-white">Could this be you?</h3>
        <p className="mt-2 text-sm text-slate-300">
          HLT is reviewing new {title.toLowerCase()} for upcoming events, content and destination programming.
        </p>
        <button className="btn-primary mt-5" onClick={onApply}>Apply to roster</button>
      </div>
    </article>
  );
}

function ApplicationModal({ onClose }) {
  const [form, setForm] = useState(EMPTY_APPLICATION);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  function updateField(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      await submissionsApi.submitRosterApplication(form);
      setMessage("Application received. HLT will review it in the next roster cycle.");
      setForm(EMPTY_APPLICATION);
    } catch {
      setMessage("Could not submit yet. The submissions backend needs to be connected.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/70 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-white/10 bg-[var(--surface)] p-5 shadow-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-brand-500">Roster application</p>
            <h2 className="mt-2 text-3xl font-semibold text-white">Apply to HLT</h2>
          </div>
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>

        <form className="mt-5 space-y-4" onSubmit={submit}>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Artist name">
              <input className="input" value={form.artist_name} onChange={(e) => updateField("artist_name", e.target.value)} required />
            </Field>
            <Field label="Email">
              <input type="email" className="input" value={form.email} onChange={(e) => updateField("email", e.target.value)} required />
            </Field>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Location">
              <input className="input" value={form.location} onChange={(e) => updateField("location", e.target.value)} />
            </Field>
            <Field label="Genre">
              <input className="input" value={form.genre} onChange={(e) => updateField("genre", e.target.value)} />
            </Field>
            <Field label="Instagram">
              <input className="input" value={form.instagram} onChange={(e) => updateField("instagram", e.target.value)} placeholder="https://..." />
            </Field>
          </div>
          <Field label="SoundCloud / Mix link">
            <input className="input" value={form.soundcloud} onChange={(e) => updateField("soundcloud", e.target.value)} placeholder="https://..." />
          </Field>
          <Field label="Bio">
            <textarea className="textarea min-h-24" value={form.bio} onChange={(e) => updateField("bio", e.target.value)} />
          </Field>
          <Field label="Why HLT?">
            <textarea className="textarea min-h-24" value={form.why_hlt} onChange={(e) => updateField("why_hlt", e.target.value)} />
          </Field>
          <button className="btn-primary" disabled={saving}>{saving ? "Sending..." : "Submit application"}</button>
          {message && <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 text-sm text-slate-200">{message}</div>}
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-wider text-slate-400">{label}</span>
      {children}
    </label>
  );
}

function normaliseSocial(value, type) {
  if (!value) return "";
  if (value.startsWith("http")) return value;
  if (type === "instagram") return `https://instagram.com/${value.replace(/^@/, "")}`;
  return value;
}
